import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  InputSignal,
  Output,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { NotificationStatus } from '@shared/enums/notification-status';
import { AppNotificationService, ModalService } from '@shared/services';
import { TaxNews } from '../../models/tax-news';
import { TaxNewsService } from '../../services/tax-news.service';

@Component({
  standalone: false,
  selector: 'gstar-tax-news-card',
  templateUrl: './tax-news-card.component.html',
  styleUrl: './tax-news-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxNewsCardComponent {
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly taxNewsService = inject(TaxNewsService);
  private readonly notificationService = inject(AppNotificationService);
  private readonly router = inject(Router);

  taxNews = input.required<TaxNews>();
  isGrid: InputSignal<boolean> = input<boolean>(false);
  @Output() reloadNews: EventEmitter<void> = new EventEmitter();

  actionMenu: ActionButton<TaxNews>[] = [
    {
      label: this.translateService.instant('shared.view'),
      icon: '',
      command: (item: TaxNews): void => {
        this.viewCommand(item);
      },
    },
    {
      label: this.translateService.instant('shared.edit'),
      icon: '',
      command: (item: TaxNews): void => {
        this.editCommand(item);
      },
    },
    {
      label: this.translateService.instant('shared.delete'),
      icon: '',
      command: (item: TaxNews): void => {
        this.deleteCommand(item);
      },
    },
  ];

  private viewCommand(item: TaxNews): void {
    this.router.navigate([
      '/country',
      item.countryId,
      'tax-news',
      'edit',
      item.countryTaxNewsId,
    ]);
  }

  private editCommand(item: TaxNews): void {
    this.router.navigate([
      '/country',
      item.countryId,
      'tax-news',
      'edit',
      item.countryTaxNewsId,
      { isEdit: true },
    ]);
  }

  private deleteCommand(item: TaxNews): void {
    this.modalService.open(
      ConfirmationModalComponent,
      {
        appModalId: 'delete-confirm-modal',
      },
      {
        title: this.translateService.instant(
          'countryInformation.deleteTaxNewsTitle'
        ),
        content: this.translateService.instant(
          'countryInformation.deleteTaxNewsContent'
        ),
        modalId: 'delete-confirm-modal',
        confirmationFn: () => this.deleteTaxNews(item),
      }
    );
  }

  deleteTaxNews(item: TaxNews): void {
    this.taxNewsService.deleteTaxNews(item.countryTaxNewsId!).subscribe(() => {
      this.notificationService.notify(
        `notifications.deleteTitleSuccess`,
        `notifications.deleteDescriptionSuccess`,
        NotificationStatus.SUCCESS,
        {
          entityName: this.translateService.instant(
            'countryInformation.taxNews'
          ),
        }
      );
      this.reloadNews.emit();
    });
  }
}
